// src/pages/Admin.js
import React, { useState, useEffect } from 'react';
import { Lock, Settings, Bell, Users, Megaphone, Loader, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { fetchSubscribers } from '../utils/airtable';

const Admin = ({ county }) => {
  const ADMIN_PASSWORD = county?.adminPassword;
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [notification, setNotification] = useState({ title: '', body: '' });
  const [subscribers, setSubscribers] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [activeSection, setActiveSection] = useState('notifications');

  const sessionKey = `votergо_admin_${county?.id || 'default'}`;

  useEffect(() => {
    const stored = sessionStorage.getItem(sessionKey);
    if (stored === 'true') setAuthenticated(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  const handleLogin = () => {
    if (ADMIN_PASSWORD && password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      sessionStorage.setItem(sessionKey, 'true');
      loadSubscribers();
    } else {
      setPasswordError('Incorrect password');
    }
  };

  const loadSubscribers = async () => {
    try {
      const tokens = await fetchSubscribers();
      setSubscribers(tokens);
    } catch (err) {
      console.log('Could not load subscribers:', err);
    }
  };

  const handleSendNotification = async () => {
    if (!notification.title || !notification.body) return;
    setSending(true);
    setResult(null);
    try {
      const response = await fetch('/.netlify/functions/send-notification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: notification.title,
          body: notification.body,
          password: ADMIN_PASSWORD,
          countyId: county?.id,
          tokens: subscribers,
        }),
      });
      const data = await response.json();
      setResult(data);
      if (data.success) {
        setNotification({ title: '', body: '' });
      }
    } catch (err) {
      setResult({ error: 'Could not send notification' });
    } finally {
      setSending(false);
    }
  };

  if (!authenticated) {
    return (
      <div style={{ maxWidth: 400, margin: '40px auto', padding: 20 }}>
        <div className="sec-head">
          <div className="sec-icon"><Lock size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
          <div>
            <div className="sec-title">Admin Login</div>
            <div className="sec-desc">{county?.name || 'VoterGO'} administration</div>
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Password</label>
          <input
            type="password"
            placeholder="Enter admin password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
            style={{ width: '100%' }}
          />
          {passwordError && <div className="error-msg" style={{ marginTop: 8 }}>{passwordError}</div>}
        </div>
        <button className="btn-red" onClick={handleLogin} style={{ width: '100%', height: 40 }}>
          Login
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="sec-head">
        <div className="sec-icon"><Settings size={20} color="currentColor" style={{ verticalAlign: 'middle' }} /></div>
        <div>
          <div className="sec-title">{county?.name || 'VoterGO'} Admin</div>
          <div className="sec-desc">Manage notifications and content</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {['notifications', 'subscribers'].map(section => (
          <button
            key={section}
            className={`btn-${activeSection === section ? 'red' : 'outline'}`}
            onClick={() => setActiveSection(section)}
            style={{ height: 36, padding: '0 16px', textTransform: 'capitalize' }}
          >
            {section === 'notifications'
              ? <><Bell size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Send Notification</>
              : <><Users size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Subscribers ({subscribers.length})</>}
          </button>
        ))}
        <button
          className="btn-outline"
          onClick={() => { sessionStorage.removeItem(sessionKey); setAuthenticated(false); }}
          style={{ height: 36, padding: '0 16px', marginLeft: 'auto' }}
        >
          Logout
        </button>
      </div>

      {activeSection === 'notifications' && (
        <div style={{ maxWidth: 560 }}>
          <div style={{ background: '#f0f4f8', borderRadius: 6, padding: '12px 14px', marginBottom: 16, fontSize: 13, color: '#0c447c' }}>
            <Megaphone size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Notifications will be sent to <strong>{subscribers.length} subscriber{subscribers.length !== 1 ? 's' : ''}</strong>
          </div>

          <div className="form-group">
            <label className="form-label">Notification title</label>
            <input
              type="text"
              placeholder="e.g. Election Day Tomorrow!"
              value={notification.title}
              onChange={e => setNotification({ ...notification, title: e.target.value })}
              style={{ width: '100%' }}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Message</label>
            <textarea
              placeholder="e.g. Don't forget to return your ballot by 7pm tonight. Drop boxes are open across the county."
              value={notification.body}
              onChange={e => setNotification({ ...notification, body: e.target.value })}
              style={{ width: '100%', height: 100, padding: '8px 12px', fontSize: 14, borderRadius: 6, border: '0.5px solid #ddd', fontFamily: 'inherit', resize: 'vertical' }}
            />
            <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
              {notification.body.length}/200 characters recommended
            </div>
          </div>

          <div style={{ background: '#f5f5f5', borderRadius: 6, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 8, fontWeight: 500 }}>Preview:</div>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <img src="/logo.png" alt="VoterGO" style={{ width: 36, height: 36, borderRadius: 8 }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{notification.title || 'Notification title'}</div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{notification.body || 'Your message here...'}</div>
              </div>
            </div>
          </div>

          <button
            className="btn-red"
            onClick={handleSendNotification}
            disabled={sending || !notification.title || !notification.body}
            style={{ height: 42, padding: '0 28px', fontSize: 14 }}
          >
            {sending
              ? <><Loader size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Sending...</>
              : <><Bell size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Send to {subscribers.length} subscribers</>}
          </button>

          {result && (
            <div className={result.error ? 'error-msg' : 'success-msg'} style={{ marginTop: 12 }}>
              {result.error
                ? <><AlertTriangle size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> {result.error}</>
                : <><CheckCircle size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> {result.message}</>}
            </div>
          )}
        </div>
      )}

      {activeSection === 'subscribers' && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: '#666' }}>{subscribers.length} active subscribers</div>
            <button className="btn-outline" onClick={loadSubscribers} style={{ height: 32, padding: '0 14px', fontSize: 12 }}>
              <RefreshCw size={16} color="currentColor" style={{ verticalAlign: 'middle' }} /> Refresh
            </button>
          </div>
          {subscribers.length === 0 ? (
            <div style={{ background: '#f5f5f5', borderRadius: 6, padding: 16, fontSize: 13, color: '#666' }}>
              No subscribers yet. Subscribers will appear here when users enable notifications.
            </div>
          ) : (
            <div style={{ fontSize: 12, color: '#666', lineHeight: 2 }}>
              {subscribers.map((token, i) => (
                <div key={i} style={{ background: '#f5f5f5', borderRadius: 4, padding: '6px 10px', marginBottom: 4, fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {i + 1}. {token.substring(0, 40)}...
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Admin; 
